/**
 * 도메인 설정 유틸리티
 * 
 * 프론트엔드와 백엔드가 같은 서버에서 서비스되므로,
 * 현재 접속한 도메인을 그대로 사용합니다.
 * 새로운 도메인 추가 시 백엔드 CORS 설정만 추가하면 됩니다.
 */

/**
 * 현재 브라우저의 호스트명 가져오기
 */
export const getCurrentHostname = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.hostname;
};

/**
 * 백엔드 API Base URL 생성
 * 현재 접속한 도메인을 그대로 사용 (도메인 매핑 불필요)
 */
export const getBackendApiBaseUrl = (): string => {
  // 로컬 개발 환경이면 Vite 프록시 사용
  if (isLocalDevelopment()) {
    return '/api';
  }

  // 현재 접속한 도메인을 그대로 사용
  const protocol = window.location.protocol;
  const hostname = getCurrentHostname();
  
  // 환경 변수에서 API Base URL 가져오기
  const envApiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  
  if (envApiBaseUrl) {
    // 환경 변수에 전체 URL이 있으면 현재 호스트명으로 교체
    const url = new URL(envApiBaseUrl);
    url.hostname = hostname;
    url.protocol = protocol;
    return url.toString();
  }
  
  // 환경 변수가 없으면 현재 호스트명 사용
  return `${protocol}//${hostname}/api`;
};

/**
 * 개발 환경 여부 확인
 */
export const isDevelopment = (): boolean => {
  return import.meta.env.VITE_APP_ENV === 'development' || 
         import.meta.env.MODE === 'development' ||
         import.meta.env.MODE === 'localdev';
};

/**
 * 로컬 개발 환경 여부 확인
 */
export const isLocalDevelopment = (): boolean => {
  return getCurrentHostname() === 'localhost' || 
         getCurrentHostname() === '127.0.0.1';
};

/**
 * URL의 도메인을 현재 호스트명으로 교체
 * OnlyOffice 콜백 URL 등에 사용
 */
export const mapUrlToCurrentDomain = (url: string): string => {
  if (!url || typeof url !== 'string') {
    return url;
  }

  try {
    const urlObj = new URL(url);
    const currentHostname = getCurrentHostname();
    
    // 현재 호스트명으로 교체
    urlObj.hostname = currentHostname;
    urlObj.protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
    return urlObj.toString();
  } catch (error) {
    // URL 파싱 실패 시 원본 반환
    console.warn('Failed to map URL to current domain:', url, error);
    return url;
  }
};

/**
 * OnlyOffice 백엔드 URL 가져오기 (문서 다운로드용)
 * OnlyOffice Document Server가 문서를 다운로드할 때 사용하는 URL
 *
 * 주의: 이 함수는 문서 URL에 사용됩니다. 콜백 URL은 getOnlyOfficeCallbackBaseUrl()을 사용하세요.
 */
export const getOnlyOfficeBackendUrl = (): string => {
  // ⚠️ 중요: OnlyOffice 컨테이너가 문서를 다운로드할 때는 Docker 내부 URL을 사용해야 함
  // Docker 내부 URL(lettagaapp-backend:3001)이 설정되어 있으면 우선 사용 (개발/운영 모두)
  const internalUrl = import.meta.env.VITE_BACKEND_INTERNAL_URL;
  if (internalUrl) {
    // Docker 내부 URL인지 확인 (lettagaapp-backend, host.docker.internal 등)
    // http://로 시작하는 URL은 Docker 내부 URL로 간주 (https://는 외부 URL)
    const isDockerInternalUrl = internalUrl.includes('lettagaapp-backend') || 
                                 internalUrl.includes('host.docker.internal') ||
                                 (internalUrl.startsWith('http://') && !internalUrl.includes('localhost'));
    
    if (isDockerInternalUrl) {
      try {
        const url = new URL(internalUrl);
        return url.origin;
      } catch (e) {
        return internalUrl;
      }
    }
  }

  // 내부 URL이 없거나 외부 URL인 경우, 외부 URL 사용
  const envUrl = import.meta.env.VITE_BACKEND_EXTERNAL_URL || internalUrl;

  if (envUrl) {
    // 로컬 개발 환경에서는 환경 변수를 그대로 사용
    // host.docker.internal은 Docker 컨테이너→호스트 통신에 필요하므로 교체하면 안 됨
    if (isLocalDevelopment()) {
      try {
        const url = new URL(envUrl);
        return url.origin;
      } catch (e) {
        return envUrl;
      }
    }

    // 배포 환경: 호스트명을 현재 접속 도메인으로 교체
    // 사용자가 .sg, .my, .ai 어디서 접속하든 세션 쿠키 도메인과 일치하도록
    // (모든 도메인이 같은 백엔드로 포워드되므로 OnlyOffice 콜백도 정상 동작)
    // ⚠️ 단, Docker 내부 URL은 위에서 이미 처리되었으므로 여기서는 외부 URL만 처리
    try {
      const url = new URL(envUrl);
      url.hostname = window.location.hostname;
      url.protocol = window.location.protocol;
      return url.origin;
    } catch (e) {
      return envUrl;
    }
  }

  // 환경 변수가 없으면 현재 origin 사용
  return window.location.origin;
};

/**
 * OnlyOffice 콜백 BASE URL 가져오기 (콜백 전용)
 * OnlyOffice Document Server가 저장 완료 시 호출하는 URL
 * Docker 환경에서는 내부 네트워크 URL을 사용해야 함
 */
export const getOnlyOfficeCallbackBaseUrl = (): string => {
  // 로컬 개발 환경에서는 기존 로직 사용
  if (isLocalDevelopment()) {
    return getOnlyOfficeBackendUrl();
  }

  // 배포 환경: 콜백 BASE URL이 설정되어 있으면 우선 사용
  // Docker 컨테이너는 외부 URL(dev.lettaga.ai)에 접근할 수 없으므로
  // Docker 내부 URL(http://lettagaapp-backend:3001)을 사용해야 함
  const callbackBaseUrl = import.meta.env.VITE_ONLYOFFICE_CALLBACK_BASE_URL;
  if (callbackBaseUrl) {
    try {
      const url = new URL(callbackBaseUrl);
      return url.origin;
    } catch (e) {
      return callbackBaseUrl;
    }
  }

  // 콜백 BASE URL이 없으면 기본 URL 사용
  return getOnlyOfficeBackendUrl();
};

/**
 * 브라우저에서 문서 파일을 로드할 때 사용하는 백엔드 URL
 * 현재 접속한 도메인을 사용 (프론트엔드와 같은 도메인)
 */
export const getBackendUrlForBrowser = (): string => {
  // 로컬 개발 환경이면 Vite 프록시 사용
  if (isLocalDevelopment()) {
    return '';
  }

  // 현재 접속한 도메인을 그대로 사용
  const protocol = window.location.protocol;
  const hostname = getCurrentHostname();
  return `${protocol}//${hostname}`;
};

/**
 * OnlyOffice 콜백 URL 가져오기
 * OnlyOffice Document Server가 접근할 수 있는 URL 사용
 * (Docker 컨테이너에서 접근 가능한 외부 URL)
 */
export const getOnlyOfficeCallbackUrl = (): string => {
  // 환경 변수에서 콜백 URL 가져오기 (OnlyOffice 서버가 접근 가능한 URL)
  const envCallbackUrl = import.meta.env.VITE_ONLYOFFICE_CALLBACK_URL;

  if (envCallbackUrl) {
    // 로컬 개발 환경에서는 환경 변수를 그대로 사용
    // host.docker.internal은 Docker 컨테이너→호스트 통신에 필요하므로 교체하면 안 됨
    if (isLocalDevelopment()) {
      return envCallbackUrl;
    }

    // 배포 환경: 콜백 URL의 호스트명을 현재 접속 도메인으로 교체
    // 사용자가 .sg, .my, .ai 어디서 접속하든 세션 쿠키 도메인과 일치하도록
    // (모든 도메인이 같은 백엔드로 포워드되므로 OnlyOffice 콜백도 정상 동작)
    try {
      const url = new URL(envCallbackUrl);
      url.hostname = window.location.hostname;
      url.protocol = window.location.protocol;
      return url.toString();
    } catch (e) {
      return envCallbackUrl;
    }
  }

  // 환경 변수가 없으면 현재 origin 기반으로 콜백 URL 생성
  return `${window.location.origin}/api/documents/callback`;
};

/**
 * OnlyOffice Document Server URL 가져오기
 * OnlyOffice Document Server는 보통 dev-onlyoffice.lettaga.ai로 고정되어 있지만,
 * 필요시 도메인 매핑을 적용할 수 있도록 함수로 제공
 */
export const getOnlyOfficeDocumentServerUrl = (): string => {
  // 환경 변수에서 OnlyOffice Document Server URL 가져오기
  const envOnlyOfficeUrl = import.meta.env.VITE_ONLYOFFICE_URL;

  if (envOnlyOfficeUrl) {
    // OnlyOffice Document Server는 보통 고정 도메인이지만, 필요시 매핑 적용
    // 현재는 그대로 반환 (OnlyOffice Document Server는 dev-onlyoffice.lettaga.ai로 고정)
    return envOnlyOfficeUrl;
  }

  // 환경 변수가 없으면 기본값 사용
  // OnlyOffice Document Server는 dev-onlyoffice.lettaga.ai로 고정
  return 'https://dev-onlyoffice.lettaga.ai';
};

/**
 * postMessage 허용 Origin 목록
 * OnlyOffice 플러그인 통신 등에서 사용
 *
 * 백엔드 CORS 설정(backend/config/cors.js)과 동기화 필요
 */
export const getAllowedMessageOrigins = (): string[] => {
  const origins = [
    // 로컬 개발 환경
    'http://localhost:3000',        // Vite 개발 서버 (frontend)
    'http://localhost:3001',        // 백엔드 (로컬)
    'http://localhost:4000',        // Vite 개발 서버 (frontend-adm)
    'http://localhost:9090',        // OnlyOffice Document Server (로컬)
    'http://localhost',             // 로컬 (포트 없이)

    // Docker 내부
    'http://onlyoffice-with-plugin', // Docker OnlyOffice
    'http://lettagaapp-backend',     // Docker 백엔드

    // 개발 환경
    'https://dev.lettaga.ai',
    'https://dev.lettaga.sg',
    'https://dev.lettaga.my',
    'https://dev-adm.lettaga.ai',
    'https://dev-onlyoffice.lettaga.ai',

    // 운영 환경
    'https://lettaga.ai',
    'https://www.lettaga.ai',
    'https://lettaga.sg',
    'https://www.lettaga.sg',
    'https://lettaga.my',
    'https://www.lettaga.my',
    'https://adm.lettaga.ai',
    'https://onlyoffice.lettaga.ai',
  ];

  // 현재 origin 추가 (항상 허용)
  if (typeof window !== 'undefined' && window.location?.origin) {
    if (!origins.includes(window.location.origin)) {
      origins.push(window.location.origin);
    }
  }

  return origins;
};

/**
 * 특정 origin이 허용된 목록에 있는지 확인
 */
export const isAllowedMessageOrigin = (origin: string): boolean => {
  // origin이 없거나 'null'인 경우 (일부 iframe에서 발생)
  if (!origin || origin === 'null') return true;

  const allowedOrigins = getAllowedMessageOrigins();
  return allowedOrigins.some(allowed => origin.startsWith(allowed));
};
